import { TablePull } from 'features/tables/tablePull';
import { useLazyVacationsQuery, useTakeToWorkMutation, useVacationsQuery } from 'app/services/edpApiV1/vacations';
import React, { useEffect, useState } from 'react';
import { dataForVacationTable, sortingDate } from 'shared/helpers/utils';
import { Badge, Popconfirm, Skeleton } from 'antd';
import { viewerModel } from 'entities/viewer';
import { useAppSelector } from 'app/store';
import { useSubordinatesQuery } from 'app/services/edpApiV1/viewer';
import { EditOutlined } from '@ant-design/icons';
import { useNotifications } from 'shared/hooks/use-notifications';
import { TConsultant, TColumns, RequestsSearch } from 'shared/types/types';
import { PresetStatusColorType } from 'antd/lib/_util/colors';
import { usePullSort } from 'shared/hooks';
import { RolesUser, SortCondition } from 'shared/enums';
import SuperviserApproveMarker from 'shared/components/superviserApproveMarker';
import { useIndicators } from 'features/indicators';
import { ONE_MINUTE } from 'shared/constants';
import { TPreparedVacationInfoData } from 'shared/types/vacations.types';
import { SearchKeysNews } from 'shared/enums/news';
import { DayOffsStatusTypesMap } from 'shared/types/dayoff.types';
import { getAllAvailableOrganizations } from 'entities/organizations/model/organizations';
import ModalVacationsSubordinates from '../modalVacationsSubordinates';
import style from './index.module.scss';

export const VacationPull = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentSize, setCurrentSize] = useState<number>(10);
  const [dataVacations, setDataVacations] = useState<TPreparedVacationInfoData[] | []>([]);
  const [openModal, setOpenModal] = useState(false);
  const [requestId, setRequestId] = useState<number>();
  const accountInfo = useAppSelector(viewerModel.me.selectors.selectViewerProfile);
  const meId = useAppSelector(viewerModel.me.selectors.meId);
  const [sortInfo, sortTrigger] = usePullSort();
  const sortOption = sortInfo.value && sortInfo.value === SortCondition.ASCEND ? SortCondition.ASC : SortCondition.DESC;
  const { data: allSubordinates } = useSubordinatesQuery({ include: 'me' });
  const [trigger, result] = useTakeToWorkMutation();
  const [search, setSearch] = useState<RequestsSearch>({ byId: undefined, fio: undefined });
  const allAvailableOrganizations = useAppSelector(getAllAvailableOrganizations);

  const { isSuccess: successTakeToWork, isError: errorTakeToWork, reset: resetTakeToWork } = result;

  const handleToWork = (id: string | number): void => {
    trigger({ requestId: id, hrId: Number(meId) });
  };

  const handleChangeResponsible = (id: number): void => {
    setRequestId(id);
    setOpenModal(true);
  };

  const columns: TColumns = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'id',
      width: 100,
    },
    {
      title: 'Дата',
      dataIndex: 'created',
      key: 'created',
      sortDirections: [SortCondition.ASCEND, SortCondition.DESCEND],
      sorter: (a, b) => sortingDate(a.created, b.created),
      onHeaderCell: () => ({
        onClick: () => sortTrigger(),
      }),
    },
    {
      title: 'ФИО',
      dataIndex: 'fio',
      key: 'fio',
    },
    {
      title: 'Вид отпуска',
      dataIndex: 'vacationType',
      key: 'vacationType',
    },
    {
      title: 'Шаг согласования',
      dataIndex: 'applyStep',
      key: 'applyStep',
    },
    {
      title: 'Тип заявления',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'Дата начала',
      dataIndex: 'startedAt',
      key: 'startedAt',
      sorter: (a, b) => sortingDate(a.startedAt, b.startedAt),
    },
    {
      title: 'Дата окончания',
      dataIndex: 'endedAt',
      key: 'endedAt',
      sorter: (a, b) => sortingDate(a.endedAt, b.endedAt),
    },
    {
      title: 'Дни',
      dataIndex: 'amountDays',
      key: 'amountDays',
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (status: {
        type?: PresetStatusColorType;
        text: string;
        isSupervisorApproved: boolean;
        steps: number | null;
      }) => {
        return (
          <>
            <span className="d-flex">
              <Badge className={style.badge} status={DayOffsStatusTypesMap[status.type || 'processing']} />
              {status.text}
            </span>
            {status.steps && !status.isSupervisorApproved && (
              <>
                <br />
                <SuperviserApproveMarker text="Не согласовано руководителем" />
              </>
            )}
          </>
        );
      },
    },
    {
      title: 'Действие',
      dataIndex: 'actions',
      key: 'actions',
      render: (_: string, record: { key: React.Key }) => (
        <Popconfirm placement="leftTop" title="Вы уверены?" onConfirm={() => handleToWork(record.key)}>
          <a>Взять в работу</a>
        </Popconfirm>
      ),
    },
  ];

  const columnsWithSubordinates: TColumns = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'id',
      width: 100,
    },
    {
      title: 'Дата',
      dataIndex: 'created',
      key: 'created',
      sortDirections: [SortCondition.ASCEND, SortCondition.DESCEND],
      sorter: (a, b) => sortingDate(a.created, b.created),
      onHeaderCell: () => ({
        onClick: () => sortTrigger(),
      }),
    },
    {
      title: 'ФИО',
      dataIndex: 'fio',
      key: 'fio',
    },
    {
      title: 'Вид отпуска',
      dataIndex: 'vacationType',
      key: 'vacationType',
    },
    {
      title: 'Шаг согласования',
      dataIndex: 'applyStep',
      key: 'applyStep',
    },
    {
      title: 'Тип заявления',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'Дата начала',
      dataIndex: 'startedAt',
      key: 'startedAt',
      sorter: (a, b) => sortingDate(a.startedAt, b.startedAt),
    },
    {
      title: 'Дата окончания',
      dataIndex: 'endedAt',
      key: 'endedAt',
      sorter: (a, b) => sortingDate(a.endedAt, b.endedAt),
    },
    {
      title: 'Дни',
      dataIndex: 'amountDays',
      key: 'amountDays',
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (status: {
        type?: PresetStatusColorType;
        text: string;
        isSupervisorApproved: boolean;
        steps: number | null;
      }) => (
        <>
          <span className="d-flex">
            <Badge className={style.badge} status={DayOffsStatusTypesMap[status.type || 'processing']} />
            {status.text}
          </span>
          {status.steps && !status.isSupervisorApproved && (
            <>
              <br />
              <SuperviserApproveMarker text="Не согласовано руководителем" />
            </>
          )}
        </>
      ),
    },
    {
      title: 'Действие',
      dataIndex: 'actions',
      key: 'actions',
      render: (_: string, record: { key: React.Key }) => (
        <Popconfirm placement="leftTop" title="Вы уверены?" onConfirm={() => handleToWork(record.key)}>
          <a>Взять в работу</a>
        </Popconfirm>
      ),
    },
  ];

  if (!allAvailableOrganizations && accountInfo.role === RolesUser.HRDirector) {
    columnsWithSubordinates.push({
      title: 'Ответственный',
      dataIndex: 'hr',
      key: 'hr',
      render: (hr: TConsultant, record: { key: React.Key }) => {
        return (
          <>
            {hr && (hr.name || hr.middleName || hr.lastName) ? (
              <>
                <span>{`${hr.lastName} ${hr.name} ${hr.middleName ?? ''}`}</span>
                <EditOutlined
                  className={style.btnChangeResponsible}
                  onClick={() => handleChangeResponsible(Number(record.key))}
                />
              </>
            ) : (
              <span className={style.changeResponsible} onClick={() => handleChangeResponsible(Number(record.key))}>
                Назначить
              </span>
            )}
          </>
        );
      },
    });
  }
  // Добавляем пуллинг обновления списка и обновляем индикаторы через хук useIndicators
  const [getVacations, { data, isSuccess, isFetching, isLoading: loadingVacations }] = useLazyVacationsQuery({
    pollingInterval: ONE_MINUTE,
  });

  useEffect(() => {
    getVacations({
      type: 'new',
      size: currentSize,
      page: currentPage,
      order: sortInfo.value ? sortOption : undefined,
      allAvailableOrganizations,
      ...search,
    });
  }, [currentPage, currentSize, getVacations, search, sortInfo.value, sortOption, allAvailableOrganizations]);

  useEffect(() => {
    if (data) {
      const dataTable = dataForVacationTable(data?.data);
      if (dataTable) {
        setDataVacations(dataTable);
      }
    }
  }, [data]);

  useEffect(() => {
    const paginationInfo = data?.pagination;
    if (dataVacations?.length === 0 && paginationInfo && currentPage !== 1) {
      // определяю текущую страницу
      const totalPages = Math.ceil(paginationInfo.total / paginationInfo.size);
      //  устанавливает текущую страницу на минимальное значение из текущей страницы и общего количества страниц,
      //  чтобы избежать установки текущей страницы на значение, превышающее общее количество страниц.
      setCurrentPage(Math.min(currentPage, totalPages));
    }
  }, [dataVacations, data]);

  useNotifications(successTakeToWork, errorTakeToWork, 'Взято в работу', 'Что-то пошло не так', resetTakeToWork);
  // подписываемся на обновление индикаторов, после обновления списка и отключаем пуллинг звпроса индикаторов
  useIndicators(isSuccess, isFetching, false);
  const onSearch = (data: string, key: SearchKeysNews) => {
    if (key === SearchKeysNews.SEARCH_ID) {
      setSearch({ fio: undefined, byId: data ? data : undefined });
    } else if (key === SearchKeysNews.SEARCH_NAME) {
      setSearch({ byId: undefined, fio: data ? data : undefined });
    } else {
      setSearch({ [key]: data ? data : undefined });
    }
  };
  return (
    <>
      {loadingVacations ? (
        <Skeleton active />
      ) : (
        <>
          {dataVacations && columns && (
            <TablePull
              dataSource={dataVacations}
              columns={
                accountInfo?.role === RolesUser.HRDirector || accountInfo?.role === RolesUser.SecondLine
                  ? columnsWithSubordinates
                  : columns
              }
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              currentSize={currentSize}
              setCurrentSize={setCurrentSize}
              total={data?.pagination?.total}
              onSearch={onSearch}
            />
          )}
          {allSubordinates && allSubordinates.length > 0 && requestId && (
            <ModalVacationsSubordinates
              allSubordinates={allSubordinates}
              openModal={openModal}
              setOpenModal={setOpenModal}
              requestId={requestId}
            />
          )}
        </>
      )}
    </>
  );
};
